import { useStore } from "@redotech/react-util/store";
import { epochSecondsToInstant } from "@redotech/util/temporal";
import { localStore } from "@redotech/web-util/storage";
import { useQueryClient } from "@tanstack/react-query";
import jwtDecode from "jwt-decode";
import {
  ReactNode,
  createContext,
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getWidgetId } from "../api";
import { buildLoginRedirect } from "../login/login-redirect";
import { shouldExchangeTrackingToken } from "../login/tracking-token-exchange";
import { CustomerContext } from "./CustomerContext";
import { UserContext } from "./user";

export interface Auth {
  teamId: string;
  userEmail?: string;
  token: string;
  /** JWT subject: `order:<id>`, `email:<address>`, or `orderless`. */
  subject?: string;
  expiration: Temporal.Instant;
}

export const AuthContext = createContext<Auth | undefined>(undefined);

export type SetAuth = (token: string | undefined) => void;

export const SetAuthContext = createContext<SetAuth | undefined>(undefined);

function getAuthTokenStore() {
  const widgetId = getWidgetId();
  if (!widgetId) {
    console.error("Missing widgetId for customer portal auth token");
    return localStore("redo.customer_auth_token");
  }

  const scopedKey = `redo.${widgetId}.customer_auth_token`;
  const legacyKey = "redo.customer_auth_token";

  // Migration: Check if we have a legacy token but no scoped token
  const scopedToken = localStorage.getItem(scopedKey);
  const legacyToken = localStorage.getItem(legacyKey);

  // safe to remove legacy check sept 2025
  if (!scopedToken && legacyToken) {
    localStorage.setItem(scopedKey, legacyToken);
    localStorage.removeItem(legacyKey);
  }

  return localStore(scopedKey);
}

/**
 * Redirect to /login if not logged in
 */
export const AuthGuard = memo(function AuthGuard({
  children,
  unauthorizedMessage,
}: {
  children: ReactNode | ReactNode[];
  unauthorizedMessage?: string;
}) {
  const auth = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const needsLogin =
    !auth ||
    shouldExchangeTrackingToken({
      search: location.search,
      pathname: location.pathname,
      sessionSubject: auth.subject,
    });

  useEffect(() => {
    if (needsLogin) {
      navigate(
        buildLoginRedirect({
          pathname: location.pathname,
          search: location.search,
          unauthorizedMessage,
        }),
      );
    }
  }, [
    needsLogin,
    location.pathname,
    navigate,
    location.search,
    unauthorizedMessage,
  ]);

  if (needsLogin) {
    return null;
  }

  return <>{children}</>;
});

/**
 * Redirect to /returns-portal if we don't have a customer id
 * We don't want a user without a customer id getting into the customer portal right now
 * If we have a customer id, wait to fully load the customer context before rendering children
 */
export const CustomerAccountAuthGuard = memo(function CustomerAccountAuthGuard({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const customerContext = useContext(CustomerContext);
  const userAuth = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (!!userAuth && !userAuth.customerId) {
      navigate("/returns-portal");
    }
  }, [userAuth?.customerId, navigate, userAuth]);

  if (!customerContext) {
    return null;
  }

  return <>{children}</>;
});

function tokenToAuth(token: string): Auth {
  const decoded: any = jwtDecode(token);
  return {
    teamId: decoded.team_id,
    userEmail: decoded.email,
    token,
    subject: decoded.sub,
    expiration: epochSecondsToInstant(decoded.exp),
  };
}

export const AuthProvider = memo(function AuthProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const authTokenStore = getAuthTokenStore();
  const [authToken, setAuthToken] = useStore(authTokenStore);
  const queryClient = useQueryClient();
  const prevAuthTokenRef = useRef(authToken);
  const auth = useMemo(
    () => (authToken === undefined ? undefined : tokenToAuth(authToken)),
    [authToken],
  );

  // Invalidate queries when the auth token changes
  useEffect(() => {
    const prevToken = prevAuthTokenRef.current;
    prevAuthTokenRef.current = authToken;
    if (authToken !== undefined && authToken !== prevToken) {
      void queryClient.invalidateQueries();
    }
  }, [authToken, queryClient]);

  useEffect(() => {
    if (!auth) {
      return;
    }
    const duration = auth.expiration.since(Temporal.Now.instant());
    if (duration.sign < 0) {
      setAuthToken(undefined);
      return;
    }
    const timeout = setTimeout(
      () => setAuthToken(undefined),
      Math.min(2_147_483_647, duration.total("milliseconds")),
    );
    return () => clearTimeout(timeout);
  }, [auth, setAuthToken]);

  return (
    <AuthContext.Provider value={auth}>
      <SetAuthContext.Provider value={setAuthToken}>
        {children}
      </SetAuthContext.Provider>
    </AuthContext.Provider>
  );
});
